import clsx from 'clsx';
import { useAtom } from 'jotai';

import { useFindSPFromMessageId } from '@/utils/hooks/conversations/useFindSPFromMessage';
import { useMessageFilter } from '@/utils/hooks/conversations/useMessageFilter';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useUser from '@/utils/hooks/swr/useUser';

import getIGUsernameTags, { getIGUsernameLink } from '@/utils/text/getIGUsernameTags';

import type { SocialInboxMessage as SocialInboxMessageType } from '@/components/pages/conversations/SocialInbox/types';

import { CAPTION, TAG } from '@/lib/manage/mentions';
import { INSTAGRAM } from '@/lib/socialProfile';
import { MESSAGE_FILTER_META } from '@/components/pages/conversations/SocialInbox/const';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import Comment from '@/components/base/Comment';
import Icon from '@/components/base/Icon';
import Mention from '@/components/base/Mention';
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';

interface SocialInboxMessageProps {
    message?: SocialInboxMessageType;
    onPostClick?: (mediaId: string) => void;
}

export const SocialInboxMessage = ({ message, onPostClick }: SocialInboxMessageProps) => {
    const { user } = useUser();
    const { formatDateTimeNoTz } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );
    const { socialProfile } = useFindSPFromMessageId({ message });
    const { messageFilter } = useMessageFilter();
    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom);
    // Important and Resolved are not social inbox types
    const socialInboxType =
        messageFilter === MESSAGE_FILTER_META.mention.key
            ? MESSAGE_FILTER_META.mention.key
            : MESSAGE_FILTER_META.comment.key;

    const opened = openedMessage && message.id === openedMessage.id;
    const isInstagram = socialProfile?.social_network_type === INSTAGRAM;

    const onOpen = () => {
        setOpenedMessage(message);
    };

    const renderUsername = () => {
        if (!message.data?.username) return;

        if (isInstagram) {
            return (
                <a
                    href={getIGUsernameLink(message.data.username)}
                    className={clsx('ml-2 text-sm hover:text-icoBlue', {
                        'text-icoBlue': !message?.is_read
                    })}
                    target="_blank"
                    rel="noopener noreferrer">
                    {message.data.username}
                </a>
            );
        }

        return <span className="font-GSemiBold text-icoBlack">{message.data.username}</span>;
    };

    const renderMentionMessage = (mention) => {
        if (mention.mentioned_in === TAG) {
            return <Mention.TagMessage username={mention.media?.username} />;
        }

        let message =
            (mention.comment?.data?.parent?.message ?? mention.comment?.data?.message) || '';

        if (mention.mentioned_in === CAPTION) {
            message = mention.media?.caption
                ?.replaceAll(new RegExp(`@${socialProfile.slug}`, 'g'), socialProfile.slug)
                ?.replaceAll(new RegExp(socialProfile.slug, 'g'), `@${socialProfile.slug}`);
        }

        const tags = getIGUsernameTags(message);

        return (
            <Comment.Message
                awsTranslation={undefined}
                message={message}
                tags={tags}
                className="text-xs"
            />
        );
    };

    const renderMessage = () => {
        if ('mentioned_in' in message) {
            return renderMentionMessage(message);
        }
        return <span className="font-GMedium text-icoGray-500">{message.data.message}</span>;
    };

    const handlePostClick = () => {
        if (!message.media?.id) return;

        onPostClick?.(message.media.id);
    };

    return (
        <div className="group relative">
            {!message.is_read && (
                <div className="absolute size-3 -translate-x-1/4 -translate-y-1/4 rounded-full border-2 border-white bg-icoBlue dark:border-icoDarkMode-deepBlack" />
            )}
            <button
                className={`flex h-20 w-full overflow-hidden rounded border-[1px] ${
                    opened
                        ? 'border-2 border-icoBlue'
                        : 'border-icoGray-300 dark:border-icoDarkMode-beton'
                }`}
                onClick={onOpen}
                key={message.id}>
                <div
                    className={`flex items-center justify-center px-1 ${
                        message.is_read
                            ? 'bg-icoGray-100 dark:bg-icoDarkMode-anthracite'
                            : 'bg-icoBlue-100 dark:bg-icoBlue/20'
                    }`}>
                    {socialProfile && (
                        <div className="flex flex-col items-center space-y-1">
                            <ProfileItem size="sm" profile={socialProfile} />
                            <span className="w-16 truncate font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {socialProfile.slug}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex h-full grow items-center space-x-3 p-3">
                    <div className="relative aspect-1 h-full shrink-0 overflow-hidden rounded-lg">
                        <Comment.Post
                            media={message.media}
                            onClick={handlePostClick}
                            displayPost
                            remoteThumbnail
                        />
                    </div>

                    <div className="flex h-full grow flex-col overflow-hidden">
                        <div className="flex w-full items-center justify-between pb-1">
                            <div className="flex items-center space-x-1">
                                <Icon
                                    icon={MESSAGE_FILTER_META[socialInboxType].icon}
                                    className="text-13 text-icoBlue"
                                />
                                <span className="font-GMedium text-xxs uppercase tracking-wider text-icoGray-500">
                                    {formatDateTimeNoTz(
                                        message.created,
                                        'weekDay_day_month_year_hour_minute'
                                    )}
                                </span>
                            </div>
                        </div>
                        <span className="grow space-x-1 overflow-hidden break-all text-left text-sm">
                            {renderUsername()}

                            {renderMessage()}
                        </span>
                    </div>
                </div>
            </button>
        </div>
    );
};
